import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Logo } from '../../components/common/Logo';
import bg from '../../assets/bg-register.jpg';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { FirebaseAuthForm } from '../../components/auth/FirebaseAuthForm';

export const LoginPage = () => {
  const [isChecking, setIsChecking] = useState(true);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, firebaseUser, isInitialized } = useAuth();

  useEffect(() => {
    const checkExistingSession = async () => {
      // Wait for auth to initialize
      if (!isInitialized) {
        return;
      }

      // Check for Firebase authentication first
      if (isAuthenticated && firebaseUser) {
        setIsChecking(false);
        // Firebase user is authenticated, navigate to home
        try {
          navigate('/home');
        } catch (error) {
          console.error('Navigation error:', error);
        }
        return;
      }

      // Check for JWT token authentication
      const token = localStorage.getItem('nuroleap_token');
      const userId = localStorage.getItem('nuroleap_user_id');
      const lastLogin = localStorage.getItem('nuroleap_last_login');

      if (token && userId) {
        try {
          // Si lastLogin es 'null' o no existe, es primera vez
          if (!lastLogin || lastLogin === 'null') {
            navigate('/onboarding');
          } else {
            // Usuario que ya ha hecho login antes, va directo a home
            navigate('/home');
          }
        } catch (error) {
          console.error('Session validation failed:', error);
          localStorage.clear();
        }
      }
      setIsChecking(false);
    };

    checkExistingSession();
  }, [navigate, isAuthenticated, firebaseUser, isInitialized]);




  if (isChecking) {
    return (
      <div className="h-screen bg-black flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-[#5CD2F4] animate-spin mx-auto" />
          <p className="text-white text-lg">Checking your session...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${bg})` }}
    >
      {/* Remove TopBar and add logo directly in content */}
      <div className="h-screen flex flex-col items-center justify-center px-3 sm:px-4 py-4 sm:py-6">
        {/* Contenedor del contenido de autenticación */}
        <div className="w-full max-w-sm sm:max-w-md space-y-4 sm:space-y-6 flex flex-col justify-center">
          {/* Logo section above titles */}
          <div className="flex justify-center mb-8">
            <div className="scale-150">
              <Logo />
            </div>
          </div>

          {/* Remove the logo section and start with titles */}
          <div className="space-y-4 text-center">
            {/* Start with titles directly */}
            <div className="space-y-2">
              <h1 className="text-2xl sm:text-3xl text-gray-300 font-bold">Login. Visualize. Become.</h1>
              <p className="text-sm sm:text-base text-gray-300">Sign in to access your account.</p>
            </div>
          </div>

          {/* Contenedor de mensajes de estado */}
          {location.state?.message && (
            <div className="text-[#5CD2F4] text-center text-sm bg-[#5CD2F4]/10 p-3 rounded-lg">
              {location.state.message}
            </div>
          )}

          {/* Firebase Authentication */}
          <div className="space-y-4 flex flex-col items-center w-full">
            {!isAuthenticated ? (
              <div className="w-full max-w-md">
                <FirebaseAuthForm 
                  mode="signin"
                  onSuccess={() => {
                    // Navigate to home - the app routing will check vision status
                    // and redirect to onboarding if needed
                    navigate('/home');
                  }}
                  onError={(error) => {
                    console.error('Login error:', error);
                  }}
                />
                <div className="mt-4 space-y-4 text-center">
                  <Link
                    to="/register"
                    className="block text-[#5CD2F4] hover:text-[#5CD2F4]/80 font-semibold hover:underline transition-colors"
                  >
                    Create Account
                  </Link>
                  <Link
                    to="/recover-password"
                    className="block text-sm text-[#5CD2F4] hover:text-[#5CD2F4]/80 hover:underline transition-colors"
                  >
                    Forgot your password?
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center text-white">
                <p>You are already signed in.</p>
                <button
                  onClick={() => navigate('/home')}
                  className="mt-4 primary-button"
                >
                  Go to Home
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};